/**
 * Shared cell creation utilities for layout strategies.
 *
 * These functions handle the common logic of creating GridCell objects
 * from run data, avoiding duplication across different layout strategies.
 */

import type {MaskOptions} from '@wandb/weave/common/types/media';
import {MediaCardType} from '@wandb/weave/common/types/media';

import {RunSignature} from '../../../../types/run';
import {getMediaMetadata} from '../../../../util/media';
import {MediaBrowserPanelConfig} from '../../common';
import {defaultLayout} from '../../utils/maskControlUtils';
import {GridCell, TransformInput} from '../types';

/**
 * Creates cells for all media keys at a given run/step/index combination.
 *
 * @param run - The run to create cells for
 * @param step - The step value (mapped step number)
 * @param mediaIndex - The media index
 * @param mediaKeys - Array of media keys to create cells for
 * @param config - Panel configuration
 * @param allMasks - Mask configuration
 * @param sliderStepValue - Optional slider step value (e.g., timestamp) before mapping to step
 * @returns Array of grid cells (may be empty if no data exists)
 */
export function createCellsForMediaKeys(
  run: TransformInput['runs'][0],
  step: number,
  mediaIndex: number,
  mediaKeys: string[],
  config: TransformInput['config'],
  allMasks: TransformInput['allMasks'],
  sliderStepValue?: number
): GridCell[] {
  const cells: GridCell[] = [];

  for (const mediaKey of mediaKeys) {
    const cell = createCell(
      run,
      step,
      mediaIndex,
      mediaKey,
      config,
      allMasks,
      sliderStepValue
    );
    if (cell) {
      cells.push(cell);
    }
  }

  return cells;
}

/**
 * Creates a single grid cell for a specific media key.
 *
 * @param run - The run to create a cell for
 * @param step - The step value (mapped step number)
 * @param mediaIndex - The media index
 * @param mediaKey - The media key to create a cell for
 * @param config - Panel configuration
 * @param allMasks - Mask configuration
 * @param sliderStepValue - Optional slider step value (e.g., timestamp) before mapping to step
 * @returns Grid cell or null if no metadata exists for this combination
 */
export function createCell(
  run: TransformInput['runs'][0],
  step: number,
  mediaIndex: number,
  mediaKey: string,
  config: TransformInput['config'],
  allMasks: TransformInput['allMasks'],
  sliderStepValue?: number
): GridCell | null {
  if (!run.history) {
    return null;
  }

  // If we have a slider step value and a custom slider key, find the history row
  // directly by matching on the slider key, not by step number
  // This ensures we get the correct metadata for each unique slider value
  const mediaSliderKey = config.mediaSliderKey ?? '_step';
  let metadata;

  if (sliderStepValue != null && mediaSliderKey !== '_step') {
    // Find history row by matching on the slider key value
    const historyRow = run.history.find(
      hr => mediaKey in hr && hr[mediaSliderKey] === sliderStepValue
    );
    metadata = historyRow?.[mediaKey] ?? null;
  } else {
    // Use the standard getMediaMetadata which matches on _step
    metadata = getMediaMetadata(run.history, mediaKey, step);
  }

  if (!metadata) {
    return null;
  }

  const mediaType = getMediaTypeFromMetadata(metadata);
  const runSignature: RunSignature = {
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
  };

  const maskOptions = getMaskOptionsForCell(mediaKey, config, allMasks);

  return {
    run,
    step,
    mediaIndex,
    mediaKey,
    tileSpec: {
      mediaType,
      run,
      mediaStep: step,
      mediaIndex,
      mediaKey,
      mediaSliderKey: config.mediaSliderKey ?? '_step',
      mediaSliderKeyValue: sliderStepValue,
      runSignature,
    },
    labels: {
      run: run.displayName || run.name,
      step,
      index: mediaIndex,
    },
    maskOptions,
  };
}

/**
 * Creates an empty cell placeholder for filling column count.
 * Used in grid layout when we need to pad rows to a specific column count.
 *
 * @param mediaKey - The media key for this empty cell
 * @returns Empty grid cell
 */
export function createEmptyCell(mediaKey: string): GridCell {
  return {
    isEmpty: true,
    mediaKey,
  };
}

/**
 * Determines the media type from metadata object.
 *
 * @param metadata - Media metadata from run history
 * @returns Media card type
 */
function getMediaTypeFromMetadata(metadata: any): MediaCardType {
  const type = metadata._type;

  if (type === 'image' || type === 'images-file') {
    return 'image';
  }
  if (type === 'video') {
    return 'video';
  }
  if (type === 'audio') {
    return 'audio';
  }
  if (type === 'html') {
    return 'html';
  }
  if (type === 'plotly') {
    return 'plotly';
  }
  if (type === 'object3D') {
    return 'object3D';
  }
  if (type === 'table') {
    return 'table';
  }
  if (type === 'molecule') {
    return 'molecule';
  }

  return 'image';
}

/**
 * Gets mask options for a cell from configuration.
 *
 * @param mediaKey - The media key
 * @param config - Panel configuration
 * @param allMasks - All available masks (used to generate default layout when not configured)
 * @returns Mask options or undefined if not configured
 */
function getMaskOptionsForCell(
  mediaKey: string,
  config: MediaBrowserPanelConfig,
  allMasks: any
): MaskOptions | undefined {
  // Extract mask keys from allMasks for this media key
  const maskKeys = Object.keys(allMasks[mediaKey] ?? {});

  // Get tileLayout configuration, falling back to defaultLayout if not configured
  const tileLayout = config.tileLayout;
  const maskConfig = tileLayout?.[mediaKey] ?? defaultLayout(maskKeys);

  if (!maskConfig.maskOptions || maskConfig.maskOptions.length === 0) {
    return undefined;
  }

  return maskConfig.maskOptions[0];
}
